/**
 * Analytics Error Reporting Utilities
 *
 * Provides centralized error reporting and logging for analytics-related errors.
 * Includes error categorization, user-friendly messaging, and external reporting.
 *
 * Requirements: 2.5, 2.6, 2.8
 */

import { uiLogger, errorLogger } from './loggerInstance';

/** Loose shape for axios/custom errors passed into analytics reporting */
type AnalyticsThrownError = {
  statusCode?: number;
  response?: { status?: number };
  type?: string;
  message?: string;
  retryAfter?: number;
};

function messageFromUnknown(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'Unknown error';
}

function stackFromUnknown(error: unknown): string | undefined {
  return error instanceof Error ? error.stack : undefined;
}

function asThrown(error: unknown): AnalyticsThrownError {
  return error as AnalyticsThrownError;
}

export interface AnalyticsErrorContext {
  operation: string;
  component?: string;
  timeRange?: {
    start: Date;
    end: Date;
    preset?: string;
  };
  userId?: string;
  retryCount?: number;
  additionalData?: Record<string, any>;
}

export interface AnalyticsErrorReport {
  id: string;
  timestamp: Date;
  type: AnalyticsErrorType;
  message: string;
  userMessage: string;
  statusCode?: number;
  context: AnalyticsErrorContext;
  stack?: string;
  retryable: boolean;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

export type AnalyticsErrorType =
  | 'NETWORK_ERROR'
  | 'RATE_LIMIT'
  | 'PERMISSION_DENIED'
  | 'VALIDATION_ERROR'
  | 'SERVER_ERROR'
  | 'AUTHENTICATION_REQUIRED'
  | 'NOT_FOUND'
  | 'TIMEOUT'
  | 'UNKNOWN_ERROR';

/**
 * Analytics Error Reporter Class
 */
export class AnalyticsErrorReporter {
  private static instance: AnalyticsErrorReporter;
  private readonly errorReports: Map<string, AnalyticsErrorReport> = new Map();
  private readonly maxReports = 100; // Keep last 100 error reports

  private constructor() {}

  public static getInstance(): AnalyticsErrorReporter {
    if (!AnalyticsErrorReporter.instance) {
      AnalyticsErrorReporter.instance = new AnalyticsErrorReporter();
    }
    return AnalyticsErrorReporter.instance;
  }

  /**
   * Report an analytics error with comprehensive logging and categorization
   */
  public reportError(error: unknown, context: AnalyticsErrorContext): AnalyticsErrorReport {
    const errorReport = this.createErrorReport(error, context);

    // Store the error report
    this.errorReports.set(errorReport.id, errorReport);

    // Clean up old reports if we exceed the limit
    if (this.errorReports.size > this.maxReports) {
      const oldestKey = this.errorReports.keys().next().value;
      if (oldestKey !== undefined) {
        this.errorReports.delete(oldestKey);
      }
    }

    // Log the error based on severity
    this.logError(errorReport);

    // Report to external services if needed
    this.reportToExternalServices(errorReport);

    return errorReport;
  }

  /**
   * Create a structured error report from an error and context
   */
  private createErrorReport(error: unknown, context: AnalyticsErrorContext): AnalyticsErrorReport {
    const id = `analytics_error_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    const timestamp = new Date();

    // Determine error type and details
    const { type, statusCode, retryable, severity } = this.categorizeError(error);

    // Generate user-friendly message
    const userMessage = this.generateUserMessage(type, error, context);

    return {
      id,
      timestamp,
      type,
      message: messageFromUnknown(error),
      userMessage,
      statusCode,
      context,
      stack: stackFromUnknown(error),
      retryable,
      severity,
    };
  }

  /**
   * Categorize error based on its characteristics
   */
  private categorizeError(error: unknown): {
    type: AnalyticsErrorType;
    statusCode?: number;
    retryable: boolean;
    severity: 'low' | 'medium' | 'high' | 'critical';
  } {
    const e = asThrown(error);
    const statusCode = e.statusCode || e.response?.status;
    const errorType = e.type;
    const message = e.message?.toLowerCase() || '';

    // Use explicit error type if available
    if (errorType) {
      switch (errorType) {
        case 'RATE_LIMIT':
          return { type: 'RATE_LIMIT', statusCode, retryable: false, severity: 'medium' };
        case 'PERMISSION_DENIED':
          return { type: 'PERMISSION_DENIED', statusCode, retryable: false, severity: 'high' };
        case 'VALIDATION_ERROR':
          return { type: 'VALIDATION_ERROR', statusCode, retryable: false, severity: 'low' };
        case 'AUTHENTICATION_REQUIRED':
          return {
            type: 'AUTHENTICATION_REQUIRED',
            statusCode,
            retryable: false,
            severity: 'high',
          };
        case 'SERVER_ERROR':
          return { type: 'SERVER_ERROR', statusCode, retryable: true, severity: 'high' };
        case 'NETWORK_ERROR':
          return { type: 'NETWORK_ERROR', statusCode, retryable: true, severity: 'medium' };
        case 'NOT_FOUND':
          return { type: 'NOT_FOUND', statusCode, retryable: false, severity: 'medium' };
      }
    }

    // Categorize based on status code
    switch (statusCode) {
      case 429:
        return { type: 'RATE_LIMIT', statusCode, retryable: false, severity: 'medium' };
      case 401:
        return { type: 'AUTHENTICATION_REQUIRED', statusCode, retryable: false, severity: 'high' };
      case 403:
        return { type: 'PERMISSION_DENIED', statusCode, retryable: false, severity: 'high' };
      case 400:
        return { type: 'VALIDATION_ERROR', statusCode, retryable: false, severity: 'low' };
      case 404:
        return { type: 'NOT_FOUND', statusCode, retryable: false, severity: 'medium' };
      case 408:
        return { type: 'TIMEOUT', statusCode, retryable: true, severity: 'medium' };
      case 500:
      case 502:
      case 503:
      case 504:
        return { type: 'SERVER_ERROR', statusCode, retryable: true, severity: 'high' };
    }

    // Categorize based on error message
    if (
      message.includes('network') ||
      message.includes('connection') ||
      message.includes('fetch')
    ) {
      return { type: 'NETWORK_ERROR', statusCode, retryable: true, severity: 'medium' };
    }

    if (message.includes('timeout')) {
      return { type: 'TIMEOUT', statusCode, retryable: true, severity: 'medium' };
    }

    if (message.includes('rate limit') || message.includes('too many')) {
      return { type: 'RATE_LIMIT', statusCode, retryable: false, severity: 'medium' };
    }

    if (
      message.includes('permission') ||
      message.includes('unauthorized') ||
      message.includes('forbidden')
    ) {
      return { type: 'PERMISSION_DENIED', statusCode, retryable: false, severity: 'high' };
    }

    if (message.includes('invalid') || message.includes('validation')) {
      return { type: 'VALIDATION_ERROR', statusCode, retryable: false, severity: 'low' };
    }

    // Default to unknown error
    return { type: 'UNKNOWN_ERROR', statusCode, retryable: true, severity: 'medium' };
  }

  /**
   * Generate user-friendly error message
   */
  private generateUserMessage(
    type: AnalyticsErrorType,
    error: unknown,
    context: AnalyticsErrorContext,
  ): string {
    const operation = context.operation || 'analytics operation';

    switch (type) {
      case 'NETWORK_ERROR':
        return 'Unable to connect to the analytics service. Please check your internet connection and try again.';

      case 'RATE_LIMIT': {
        const retryAfterSec = asThrown(error).retryAfter;
        const retryAfter = retryAfterSec ? Math.ceil(retryAfterSec / 60) : 5;
        return `You've made too many analytics requests. Please wait ${retryAfter} minutes before trying again.`;
      }

      case 'PERMISSION_DENIED':
        return "You don't have permission to view analytics data. Please contact your administrator for access.";

      case 'VALIDATION_ERROR':
        if (context.timeRange) {
          return 'The selected time range is invalid. Please choose a valid date range and try again.';
        }
        return 'The request parameters are invalid. Please check your selection and try again.';

      case 'AUTHENTICATION_REQUIRED':
        return 'Your session has expired. Please log in again to view analytics data.';

      case 'SERVER_ERROR':
        return 'The analytics service is temporarily unavailable. Our team has been notified and is working on a fix.';

      case 'NOT_FOUND':
        return 'The requested analytics data could not be found. This feature may not be available for your account.';

      case 'TIMEOUT':
        return 'The analytics request timed out. Please try again with a smaller date range.';

      default:
        return `An unexpected error occurred while ${operation}. Please try again or contact support if the problem persists.`;
    }
  }

  /**
   * Log error based on severity level
   */
  private logError(report: AnalyticsErrorReport): void {
    const logData = {
      errorId: report.id,
      type: report.type,
      operation: report.context.operation,
      component: report.context.component,
      statusCode: report.statusCode,
      retryCount: report.context.retryCount,
      timeRange: report.context.timeRange,
      severity: report.severity,
    };

    switch (report.severity) {
      case 'critical':
        errorLogger.error(`CRITICAL Analytics Error: ${report.message}`, logData);
        break;
      case 'high':
        errorLogger.error(`HIGH Analytics Error: ${report.message}`, logData);
        break;
      case 'medium':
        errorLogger.warn(`MEDIUM Analytics Error: ${report.message}`, logData);
        break;
      case 'low':
        uiLogger.info(`LOW Analytics Error: ${report.message}`, logData);
        break;
    }
  }

  /**
   * Report to external error tracking services
   */
  private reportToExternalServices(report: AnalyticsErrorReport): void {
    // Only report high and critical errors to external services
    if (report.severity === 'high' || report.severity === 'critical') {
      try {
        // Example: Report to Sentry, LogRocket, or other error tracking service
        // if (window.Sentry) {
        //   window.Sentry.captureException(new Error(report.message), {
        //     tags: {
        //       component: 'analytics',
        //       errorType: report.type,
        //       severity: report.severity,
        //     },
        //     extra: {
        //       errorId: report.id,
        //       context: report.context,
        //       statusCode: report.statusCode,
        //     },
        //   });
        // }

        uiLogger.info('Error reported to external services', {
          errorId: report.id,
          type: report.type,
          severity: report.severity,
        });
      } catch (reportingError: unknown) {
        errorLogger.error('Failed to report error to external services', {
          originalError: report.id,
          reportingError:
            reportingError instanceof Error ? reportingError.message : String(reportingError),
        });
      }
    }
  }

  /**
   * Get error statistics for monitoring
   */
  public getErrorStats(): {
    totalErrors: number;
    errorsByType: Record<AnalyticsErrorType, number>;
    errorsBySeverity: Record<string, number>;
    recentErrors: AnalyticsErrorReport[];
  } {
    const reports = Array.from(this.errorReports.values());

    const errorsByType = reports.reduce(
      (acc, report) => {
        acc[report.type] = (acc[report.type] || 0) + 1;
        return acc;
      },
      {} as Record<AnalyticsErrorType, number>,
    );

    const errorsBySeverity = reports.reduce(
      (acc, report) => {
        acc[report.severity] = (acc[report.severity] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    const reportsNewestFirst = [...reports].sort(
      (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
    );
    const recentErrors = reportsNewestFirst.slice(0, 10);

    return {
      totalErrors: reports.length,
      errorsByType,
      errorsBySeverity,
      recentErrors,
    };
  }

  /**
   * Clear all error reports
   */
  public clearErrorReports(): void {
    this.errorReports.clear();
    uiLogger.info('Analytics error reports cleared');
  }

  /**
   * Get specific error report by ID
   */
  public getErrorReport(id: string): AnalyticsErrorReport | undefined {
    return this.errorReports.get(id);
  }
}

/**
 * Convenience function to report analytics errors
 */
export const reportAnalyticsError = (
  error: unknown,
  context: AnalyticsErrorContext,
): AnalyticsErrorReport => {
  const reporter = AnalyticsErrorReporter.getInstance();
  return reporter.reportError(error, context);
};

/**
 * Convenience function to get error statistics
 */
export const getAnalyticsErrorStats = () => {
  const reporter = AnalyticsErrorReporter.getInstance();
  return reporter.getErrorStats();
};

export default AnalyticsErrorReporter;
